import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, PersonAddAlt1, TaskAlt } from "@mui/icons-material";
import {
  useConfirmDelegationLog,
  useViewVersion,
} from "@/api/hooks/documents";
import type {
  DelegationLogPersonnelEntry,
  DocumentValidation,
} from "@/api/types";
import { DocumentVersionViewer } from "./DocumentVersionViewer";

interface Props {
  documentId: string;
  versionId: string;
  contentType: string;
  versionLabel: string;
  validation: DocumentValidation;
  onConfirmed: (updated: DocumentValidation) => void;
  onCancel: () => void;
}

interface MatchCandidate {
  investigator_id: string;
  first_name: string;
  last_name: string;
  score: number;
}

interface TaskLegendEntry {
  code: string;
  name: string;
}

interface ExtractedParticipant {
  fname: string | null;
  lname: string;
  study_role: string;
  study_tasks: string[];
  study_task_codes: string[];
  valid_from: string | null;
  valid_to: string | null;
  signed: boolean;
  pi_signed: boolean;
  is_investigator: boolean;
  match_candidates: MatchCandidate[];
}

interface ExtractedPayload {
  protocol_number: string;
  site_name: string | null;
  principal_investigator: string;
  task_legend: TaskLegendEntry[];
  participants: ExtractedParticipant[];
  extraction_notes: string[];
  /** True when the extracted protocol clearly doesn't match the
   * participation's study (e.g. uploaded a Liver-study log to a
   * Gastro participation). */
  protocol_mismatch?: boolean;
  expected_protocol?: string;
  expected_study_name?: string;
}

interface FormRow extends ExtractedParticipant {
  /** Local: which investigator the operator picked. "create-new" or
   * an existing investigator_id, or "" while undecided. */
  match_choice: string;
}

function toFormRow(row: ExtractedParticipant): FormRow {
  const top = row.match_candidates[0];
  return {
    ...row,
    match_choice:
      top && top.score >= 85
        ? top.investigator_id
        : row.is_investigator
          ? "create-new"
          : "",
  };
}

/**
 * Delegation Log review panel: PDF on the left, editable roster table
 * on the right. Each row has fname/lname/role/tasks/dates editable;
 * PI/Sub-I rows additionally show a "match an existing investigator OR
 * create a new one" dropdown. Confirm posts the final roster.
 */
export function DelegationLogReviewPanel({
  documentId,
  versionId,
  contentType,
  versionLabel,
  validation,
  onConfirmed,
  onCancel,
}: Props) {
  const view = useViewVersion();
  const confirm = useConfirmDelegationLog(documentId);

  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const extracted = (validation.extracted_value || {}) as Partial<ExtractedPayload>;
  const protocol = extracted.protocol_number || "(no protocol number)";
  const siteName = extracted.site_name || null;
  const piName = extracted.principal_investigator || "(no PI on form header)";
  const taskLegend = extracted.task_legend ?? [];
  const extractionNotes = extracted.extraction_notes ?? [];
  const protocolMismatch = extracted.protocol_mismatch === true;

  const initialRows = useMemo<FormRow[]>(
    () => (extracted.participants ?? []).map(toFormRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [validation.id],
  );

  const [rows, setRows] = useState<FormRow[]>(initialRows);
  // Gate confirmation when the extracted protocol doesn't match the
  // participation's study — operator has to explicitly check the
  // "I've reviewed this" box. Catches the wrong-file-to-wrong-study
  // mistake (Liver log dropped onto a Gastro participation).
  const [mismatchAck, setMismatchAck] = useState(false);

  useEffect(() => {
    let active = true;
    setViewerError(null);
    view
      .mutateAsync({ documentId, versionId })
      .then((res) => {
        if (active) setViewerUrl(res.download_url);
      })
      .catch((e) => {
        if (active)
          setViewerError(e instanceof Error ? e.message : "Could not open viewer");
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, versionId]);

  const update = (i: number, patch: Partial<FormRow>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const removeRow = (i: number) =>
    setRows((rs) => rs.filter((_, idx) => idx !== i));

  const addBlankRow = () =>
    setRows((rs) => [
      ...rs,
      {
        fname: "",
        lname: "",
        study_role: "",
        study_tasks: [],
        study_task_codes: [],
        valid_from: null,
        valid_to: null,
        signed: false,
        pi_signed: false,
        is_investigator: false,
        match_candidates: [],
        match_choice: "",
      },
    ]);

  const handleConfirm = async () => {
    setError(null);
    for (const r of rows) {
      if (!r.lname.trim() || !r.study_role.trim()) {
        setError(
          `Every row needs at least a last name and a role (row "${r.fname ?? ""} ${r.lname}").`,
        );
        return;
      }
      if (r.is_investigator) {
        if (r.match_choice === "create-new") {
          // Server defaults investigator_first/last_name to row's
          // fname/lname when omitted — but if fname is empty, the
          // operator has to give us at least a last name to create
          // the investigator with (which we already validated above).
          if (!r.lname.trim()) {
            setError(`Investigator "${r.fname ?? ""} ${r.lname}" needs a last name.`);
            return;
          }
        } else if (!r.match_choice) {
          setError(
            `Pick a match (or "create new") for investigator "${r.fname ?? ""} ${r.lname}".`,
          );
          return;
        }
      }
    }

    const payload: DelegationLogPersonnelEntry[] = rows.map((r) => {
      const useExisting =
        r.is_investigator &&
        r.match_choice !== "" &&
        r.match_choice !== "create-new";
      return {
        fname: r.fname?.trim() || null,
        lname: r.lname.trim(),
        study_role: r.study_role.trim(),
        study_tasks: r.study_tasks,
        study_task_codes: r.study_task_codes,
        valid_from: r.valid_from || null,
        valid_to: r.valid_to || null,
        signed: r.signed,
        pi_signed: r.pi_signed,
        is_investigator: r.is_investigator,
        investigator_id: useExisting ? r.match_choice : null,
        // Server defaults to row's fname/lname when create-new and no
        // explicit override is sent. We pass them through anyway so
        // the operator can edit the investigator name independently
        // of the row's printed name in a follow-up.
        investigator_first_name:
          r.is_investigator && !useExisting ? r.fname?.trim() || null : null,
        investigator_last_name:
          r.is_investigator && !useExisting ? r.lname.trim() : null,
      };
    });

    try {
      const res = await confirm.mutateAsync({
        validationId: validation.id,
        payload: { personnel: payload },
      });
      onConfirmed(res.validation);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Confirm failed");
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Bedrock pulled the personnel roster from the delegation log
        (protocol <strong>{protocol}</strong>
        {siteName ? (
          <>
            , site <strong>{siteName}</strong>
          </>
        ) : null}
        , PI <strong>{piName}</strong>). Review each row — match PI /
        Sub-I to existing investigators if we already have them, or
        create new ones. Confirm to add Location personnel + PI/Sub-I
        assignments.
      </Alert>

      {protocolMismatch && (
        <Alert severity="error" icon={false}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Protocol mismatch — possible wrong upload
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            The form's protocol reads <strong>"{extracted.protocol_number || "(blank)"}"</strong>
            , but this participation is on{" "}
            <strong>
              {extracted.expected_protocol ||
                extracted.expected_study_name ||
                "(unknown)"}
            </strong>
            . Make sure you uploaded the right delegation log against
            the right study before confirming — the personnel below
            will be tied to{" "}
            <strong>{extracted.expected_study_name ?? "this study"}</strong>.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              size="small"
              checked={mismatchAck}
              onChange={(e) => setMismatchAck(e.target.checked)}
            />
            <Typography variant="body2">
              I've reviewed this and want to proceed anyway
            </Typography>
          </Box>
        </Alert>
      )}

      {extractionNotes.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            Bedrock flagged the following for review:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {extractionNotes.map((note, i) => (
              <li key={i}>
                <Typography variant="body2">{note}</Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(360px, 1fr) 2fr" },
          gap: 2,
        }}
      >
        <Box>
          {viewerError && <Alert severity="error">{viewerError}</Alert>}
          {!viewerUrl && !viewerError && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {viewerUrl && (
            <DocumentVersionViewer
              url={viewerUrl}
              contentType={contentType}
              fileName="Delegation log"
              versionLabel={versionLabel}
              onClose={onCancel}
            />
          )}
          {taskLegend.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Task legend (from the form)
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                {taskLegend.map((entry) => (
                  <Chip
                    key={entry.code}
                    size="small"
                    label={`${entry.code} · ${entry.name}`}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 32 }} />
                <TableCell>First name</TableCell>
                <TableCell>Last name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Investigator?</TableCell>
                <TableCell>Match / Create</TableCell>
                <TableCell>Tasks (codes)</TableCell>
                <TableCell>Valid from</TableCell>
                <TableCell>Valid to</TableCell>
                <TableCell>Signed</TableCell>
                <TableCell>PI signed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    <Tooltip title="Remove row">
                      <IconButton size="small" onClick={() => removeRow(i)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={r.fname ?? ""}
                      onChange={(e) =>
                        update(i, { fname: e.target.value || null })
                      }
                      sx={{ minWidth: 110 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={r.lname}
                      onChange={(e) => update(i, { lname: e.target.value })}
                      sx={{ minWidth: 130 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={r.study_role}
                      onChange={(e) =>
                        update(i, { study_role: e.target.value })
                      }
                      sx={{ minWidth: 100 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={r.is_investigator}
                      onChange={(e) =>
                        update(i, {
                          is_investigator: e.target.checked,
                          match_choice: e.target.checked
                            ? r.match_choice || "create-new"
                            : "",
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {r.is_investigator ? (
                      <Select
                        size="small"
                        value={r.match_choice}
                        onChange={(e) =>
                          update(i, { match_choice: e.target.value as string })
                        }
                        displayEmpty
                        sx={{ minWidth: 200 }}
                      >
                        <MenuItem value="">— pick —</MenuItem>
                        {r.match_candidates.map((c) => (
                          <MenuItem
                            key={c.investigator_id}
                            value={c.investigator_id}
                          >
                            {c.first_name} {c.last_name}
                            <Chip
                              size="small"
                              label={`${c.score}%`}
                              sx={{ ml: 1 }}
                              color={c.score >= 85 ? "success" : "default"}
                            />
                          </MenuItem>
                        ))}
                        <MenuItem value="create-new">
                          <PersonAddAlt1 fontSize="small" sx={{ mr: 0.5 }} />
                          Create new
                        </MenuItem>
                      </Select>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        n/a
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        r.study_tasks.length > 0
                          ? r.study_tasks.join(", ")
                          : "No tasks decoded"
                      }
                    >
                      <TextField
                        size="small"
                        placeholder="1,2,3,…"
                        value={r.study_task_codes.join(",")}
                        onChange={(e) =>
                          update(i, {
                            study_task_codes: e.target.value
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean),
                          })
                        }
                        sx={{ width: 110 }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      value={r.valid_from ?? ""}
                      onChange={(e) =>
                        update(i, { valid_from: e.target.value || null })
                      }
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="date"
                      value={r.valid_to ?? ""}
                      onChange={(e) =>
                        update(i, { valid_to: e.target.value || null })
                      }
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={r.signed}
                      onChange={(e) => update(i, { signed: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={r.pi_signed}
                      onChange={(e) =>
                        update(i, { pi_signed: e.target.checked })
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={addBlankRow}
              disabled={confirm.isPending}
            >
              Add row
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onCancel} disabled={confirm.isPending}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={
                confirm.isPending ||
                rows.length === 0 ||
                (protocolMismatch && !mismatchAck)
              }
              startIcon={
                confirm.isPending ? <CircularProgress size={14} /> : <TaskAlt />
              }
            >
              {confirm.isPending ? "Saving…" : "Confirm roster"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
}
